import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { INVITE_TEMPLATES, fillInviteTemplate, type InviteTemplateId } from '../lib/inviteTemplates';
import { BrandMark } from '../components/BrandMark';
import { VenueSearchInput } from '../components/VenueSearchInput';
import { GoogleButton } from '../components/GoogleButton';

const STEPS = ['חשבון', 'חתונה', 'הזמנה'] as const;

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [venueLat, setVenueLat] = useState<number | null>(null);
  const [venueLng, setVenueLng] = useState<number | null>(null);
  const [totalSeats, setTotalSeats] = useState('120');
  const [maxPlus, setMaxPlus] = useState('19');

  const [templateId, setTemplateId] = useState<InviteTemplateId>('classic');
  const [customText, setCustomText] = useState('');

  const dateLabel = useMemo(() => {
    if (!eventDate) return '';
    try {
      return new Date(eventDate).toLocaleDateString('he-IL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return eventDate;
    }
  }, [eventDate]);

  const venueLine = venueName
    ? `📍 ${venueName}${venueAddress ? `, ${venueAddress}` : ''}\n`
    : venueAddress
      ? `📍 ${venueAddress}\n`
      : '';

  const coupleLabel = fullName.trim() || eventName.trim() || 'הזוג';

  const preview = useMemo(() => {
    if (templateId === 'custom') {
      return (
        customText
          .replace(/\{couple\}/g, coupleLabel)
          .replace(/\{date\}/g, dateLabel)
          .replace(/\{venue\}/g, venueLine)
          .replace(/\{link\}/g, 'https://…/i/…')
          .replace(/\{name\}/g, 'ישראל ישראלי') || 'כתבו טקסט מותאם…'
      );
    }
    const t = INVITE_TEMPLATES.find((x) => x.id === templateId);
    if (!t) return '';
    return fillInviteTemplate(t.body, {
      couple: coupleLabel,
      date: dateLabel,
      venue: venueLine,
      link: 'https://…/i/…',
    }).replace(/\{name\}/g, 'ישראל ישראלי');
  }, [templateId, customText, coupleLabel, dateLabel, venueLine]);

  function validateStep(): boolean {
    if (step === 0) {
      if (!email.trim() || !password || password.length < 6) {
        toast.error('אימייל וסיסמה (לפחות 6 תווים) נדרשים');
        return false;
      }
      if (!fullName.trim()) {
        toast.error('מלאו שם הזוג');
        return false;
      }
      return true;
    }
    if (step === 1) {
      if (!eventName.trim() || !eventDate) {
        toast.error('שם חתונה ותאריך נדרשים');
        return false;
      }
      const seats = Number(totalSeats);
      if (!Number.isFinite(seats) || seats < 1) {
        toast.error('כמות מוזמנים צריכה להיות מספר חיובי');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (templateId === 'custom' && !customText.trim()) {
        toast.error('כתבו טקסט הזמנה או בחרו תבנית');
        return false;
      }
      return true;
    }
    return true;
  }

  function next() {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function back() {
    setStep((s) => Math.max(s - 1, 0));
  }

  async function submit() {
    if (!validateStep()) return;
    setBusy(true);
    try {
      await register({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        phone_primary: phone.trim() || undefined,
        event: {
          name: eventName.trim(),
          event_date: eventDate,
          event_time: eventTime || undefined,
          venue_name: venueName.trim() || undefined,
          venue_address: venueAddress.trim() || undefined,
          venue_lat: venueLat ?? undefined,
          venue_lng: venueLng ?? undefined,
          total_seats: Number(totalSeats),
          max_plus_ones: Number(maxPlus) || 19,
          invitation_template: templateId,
          invitation_text: templateId === 'custom' ? customText.trim() : undefined,
        },
      });
      toast.success('החשבון נוצר — בהצלחה!');
      navigate('/');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'שגיאה בהרשמה');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div dir="rtl" className="min-h-full flex items-center justify-center px-4 py-10 sm:px-8 bg-paper">
      <div className="w-full max-w-xl page-enter">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-5">
            <BrandMark size="md" showTagline={false} />
          </div>
          <Link
            to="/login"
            className="text-[13px] text-ink-faint hover:text-sage-600 inline-flex items-center gap-1 mb-4 cursor-pointer"
          >
            <ArrowRight size={14} aria-hidden /> חזרה להתחברות
          </Link>
          <h1 className="font-sans text-3xl sm:text-4xl font-bold text-ink tracking-tight">
            הרשמת זוג
          </h1>
          <p className="text-ink-mute mt-1.5 text-[15px]">
            חשבון, פרטי חתונה, כמות מוזמנים ובחירת הודעת הזמנה
          </p>
        </div>

        <div className="flex gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i <= step
                    ? 'bg-gradient-to-l from-sage-500 to-sage-700 shadow-[0_2px_6px_-1px_rgba(163,106,122,0.5)]'
                    : 'bg-line shadow-[inset_0_1px_2px_rgba(45,41,38,0.06)]'
                }`}
              />
              <p className={`mt-2 text-[11px] font-medium ${i === step ? 'text-sage-700' : 'text-ink-faint'}`}>
                {label}
              </p>
            </div>
          ))}
        </div>

        <div className="surface !shadow-deep p-5 sm:p-7 space-y-4 animate-scale-in">
          {step === 0 && (
            <>
              <GoogleButton onClick={loginWithGoogle} disabled={busy} label="הרשמה עם Google" />
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-line" />
                <span className="text-[12px] text-ink-faint font-medium">או עם אימייל</span>
                <div className="h-px flex-1 bg-line" />
              </div>
              <div>
                <label className="label" htmlFor="reg-name">שם הזוג</label>
                <input
                  id="reg-name"
                  className="field"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="דנה ויוסי"
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="reg-email">אימייל</label>
                <input
                  id="reg-email"
                  type="email"
                  className="field"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  dir="ltr"
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="reg-pass">סיסמה</label>
                <input
                  id="reg-pass"
                  type="password"
                  className="field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  dir="ltr"
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="reg-phone">טלפון (אופציונלי)</label>
                <input
                  id="reg-phone"
                  className="field"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  dir="ltr"
                  placeholder="050-0000000"
                />
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <label className="label" htmlFor="ev-name">שם החתונה</label>
                <input
                  id="ev-name"
                  className="field"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder="חתונת דנה ויוסי"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="ev-date">תאריך</label>
                  <input
                    id="ev-date"
                    type="date"
                    className="field"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="ev-time">שעה (אופציונלי)</label>
                  <input
                    id="ev-time"
                    type="time"
                    className="field"
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="ev-venue">מקום</label>
                <VenueSearchInput
                  id="ev-venue"
                  value={venueName}
                  onChange={(text) => {
                    setVenueName(text);
                    // הקלדה ידנית מבטלת מיקום שנבחר קודם
                    setVenueLat(null);
                    setVenueLng(null);
                  }}
                  onSelect={(place) => {
                    setVenueName(place.name);
                    setVenueAddress(place.address);
                    setVenueLat(place.lat);
                    setVenueLng(place.lng);
                  }}
                />
                {venueLat != null && (
                  <p className="mt-1 text-[11.5px] text-sage-700 flex items-center gap-1">
                    <Check size={12} aria-hidden /> זוהה עסק מ-Google — המיקום המדויק יישמר למפת הטרמפים
                  </p>
                )}
              </div>
              <div>
                <label className="label" htmlFor="ev-addr">כתובת</label>
                <input
                  id="ev-addr"
                  className="field"
                  value={venueAddress}
                  onChange={(e) => setVenueAddress(e.target.value)}
                  placeholder="עיר / כתובת מלאה"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="ev-seats">כמות מוזמנים משוערת</label>
                  <input
                    id="ev-seats"
                    type="number"
                    min={1}
                    max={2000}
                    className="field"
                    value={totalSeats}
                    onChange={(e) => setTotalSeats(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="ev-plus">מלווים מקסימלי לאורח</label>
                  <input
                    id="ev-plus"
                    type="number"
                    min={0}
                    max={19}
                    className="field"
                    value={maxPlus}
                    onChange={(e) => setMaxPlus(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-[13px] text-ink-mute">בחרו תבנית להודעת WhatsApp (אפשר לערוך אחר כך)</p>
              <div className="grid gap-2">
                {INVITE_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplateId(t.id)}
                    className={`text-start px-4 py-3.5 min-h-touch rounded-2xl border transition-all duration-200 cursor-pointer focus-visible:shadow-focus focus-visible:outline-none ${
                      templateId === t.id
                        ? 'border-sage-500 bg-sage-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_6px_16px_-6px_rgba(163,106,122,0.4)] -translate-y-0.5'
                        : 'border-line hover:border-sage-300 hover:bg-white hover:-translate-y-0.5 hover:shadow-lift'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink text-[14px]">{t.title}</span>
                      {templateId === t.id && <Check size={16} className="text-sage-600" />}
                    </div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setTemplateId('custom')}
                  className={`text-start px-4 py-3.5 min-h-touch rounded-2xl border transition-all duration-200 cursor-pointer focus-visible:shadow-focus focus-visible:outline-none ${
                    templateId === 'custom'
                      ? 'border-sage-500 bg-sage-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_6px_16px_-6px_rgba(163,106,122,0.4)] -translate-y-0.5'
                      : 'border-line hover:border-sage-300 hover:bg-white hover:-translate-y-0.5 hover:shadow-lift'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-ink text-[14px]">מותאם אישית</span>
                    {templateId === 'custom' && <Check size={16} className="text-sage-600" />}
                  </div>
                </button>
              </div>

              {templateId === 'custom' && (
                <textarea
                  className="field min-h-[140px]"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder={'השתמשו ב-{name} לשם האורח, {couple}, {date}, {venue}'}
                />
              )}

              <div>
                <p className="label mb-1.5">תצוגה מקדימה</p>
                <pre className="whitespace-pre-wrap text-[13px] leading-relaxed bg-paper-mist border border-line rounded-xl p-4 text-ink font-sans">
                  {preview}
                </pre>
              </div>
            </>
          )}

          <div className="flex gap-2 pt-2">
            {step > 0 && (
              <button type="button" onClick={back} className="btn-secondary flex-1">
                הקודם
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} className="btn-primary flex-1">
                המשך
                <ArrowLeft size={16} />
              </button>
            ) : (
              <button type="button" onClick={() => void submit()} disabled={busy} className="btn-primary flex-1">
                {busy ? 'יוצרים…' : 'סיום הרשמה'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
